import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTheme } from '@material-ui/core';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { FeedsMessageView, Loader } from '@orijinworks/frontend-commons';
import { useSelector } from 'react-redux';
import { useFeedAuditConversationsHook } from './useFeedAuditConversationsHook';
import { FeedMembersAndReceiptsInfoModal } from '../../../../shared/components';
import { exportButtonStyles, exportButtonTypographyStyles } from '../FeedDrawer.styles';

/**
 * Component that displays conversation messages in a drawer for audit purposes
 * 
 * @param {Object} props - Component props
 * @param {Object} props.conversationData - Conversation data object containing messageId (required)
 * @returns {JSX.Element} The FeedAuditConversations component
 */
const FeedAuditConversations = React.memo(({ conversationData }) => {
  const { 
    messages, 
    selectedFeed, 
    isLoading, 
    isLoadingMore,
    hasMoreMessages,
    showNewMessagesIndicator,
    error,
    loadMoreMessages,
    loadNewerMessages,
    readReceiptsModal,
    isExporting,
    handleExportConversation
  } = useFeedAuditConversationsHook(conversationData);
  
  // Get user details from Redux store for userProps
  const { userDetail } = useSelector((state) => state.app || {});
  
  // Get theme for modal icon color
  const theme = useTheme();

  // Build userProps for FeedsMessageView - must be before early returns
  const userProps = {
    userId: userDetail?.userId,
    firstName: userDetail?.firstName || '',
    lastName: userDetail?.lastName || '',
  };

  // No-op handlers for read-only mode - must be before early returns
  const noOpHandler = () => {};

  // Show error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        padding="20px"
        role="alert"
        aria-label="Error loading conversation messages"
      >
        <Typography variant="body1" color="error">
          Failed to load conversation messages. Please try again.
        </Typography>
      </Box>
    );
  }

  // Show loading state
  if (isLoading || !selectedFeed) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        padding="20px"
        role="status"
        aria-label="Loading conversation messages"
      >
        <Typography variant="body1">
          Loading conversation messages...
        </Typography>
      </Box>
    );
  }


  return (
    <>
      <Loader isLoading={isExporting} />
      {/* Export Button */}
      {selectedFeed && (
        <Box
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            zIndex: 1,
          }}
        >
          <Button
            onClick={handleExportConversation}
            variant="outlined"
            startIcon={<SaveAltIcon sx={{ color: '#0F48B8' }} />}
            sx={exportButtonStyles}
            aria-label="Export conversation"
          >
            <Typography
              sx={exportButtonTypographyStyles}
            >
              Export Conversation
            </Typography>
          </Button>
        </Box>
      )}
      <FeedsMessageView
        messages={messages}
        selectedFeed={selectedFeed}
        isLoading={isLoading || isLoadingMore}
        onBackHandler={noOpHandler}
        onLoadMoreHandler={loadMoreMessages}
        onMessageSendHandler={noOpHandler}
        onSendHandler={noOpHandler}
        editorProps={null}
        showEditor={false}
        userProps={userProps}
        showSettingsIcon={false}
        onEditSettingsHandler={noOpHandler}
        onFeedSettingEditHandler={noOpHandler}
        showNewMessagesIndicator={showNewMessagesIndicator}
        onNewMessagesIndicatorDismiss={noOpHandler}
        onNewMessageIndicatorHandler={loadNewerMessages}
        hasMoreMessages={hasMoreMessages}
        shouldScrollToBottom={false}
        shouldShowLoadMoreButton={hasMoreMessages}
        shouldShowBackButton={false}
      />
      {/* Read Receipts Modal */}
      {readReceiptsModal && readReceiptsModal.open && (
        <FeedMembersAndReceiptsInfoModal
          open={readReceiptsModal.open}
          onClose={readReceiptsModal.onClose}
          heading={readReceiptsModal.heading}
          badgeLabel={readReceiptsModal.badgeLabel}
          members={readReceiptsModal.members}
          headingIcon={
            <DoneAllIcon
              fontSize="small"
              sx={{ color: theme?.palette?.colors?.blueMain }}
            />
          }
          showFooter={false}
          styles={{
            mainWrapperStyles: {
              maxHeight: '450px',
              overflowY: 'auto',
              height: 'auto !important',
              borderBottom: 'none !important'
            }
          }}
        />
      )}
    </>
  );
});

FeedAuditConversations.displayName = 'FeedAuditConversations';

export default FeedAuditConversations;

